/**
 * Utilitaires de détection automatique du pays de l'utilisateur
 *
 * Stratégie en cascade :
 * 1. Détection via langue du navigateur (navigator.language)
 * 2. Fallback sur géolocalisation IP (ipapi.co - gratuit)
 * 3. Fallback sur pays par défaut (BE)
 *
 * @module countryDetection
 */

import React from 'react';

/**
 * Map des locales navigateur vers codes pays ISO
 *
 * Format: "langue-PAYS" → "PAYS"
 */
const LOCALE_TO_COUNTRY = {
  // Belgique
  'fr-BE': 'BE',
  'nl-BE': 'BE',
  'de-BE': 'BE',
  // France
  'fr-FR': 'FR',
  'fr-CA': 'CA',
  'fr-CH': 'CH',
  // Pays-Bas
  'nl-NL': 'NL',
  // Allemagne
  'de-DE': 'DE',
  'de-AT': 'AT',
  'de-CH': 'CH',
  // Royaume-Uni
  'en-GB': 'GB',
  'en-IE': 'IE',
  // USA
  'en-US': 'US',
  // Espagne
  'es-ES': 'ES',
  // Italie
  'it-IT': 'IT',
  // Luxembourg
  'fr-LU': 'LU',
  'de-LU': 'LU',
};

/**
 * Pays par défaut si détection échoue
 */
const DEFAULT_COUNTRY = 'BE';

/**
 * Cache pour éviter les appels API répétés
 */
let cachedCountry = null;

/**
 * Détecte le pays via la langue du navigateur
 *
 * @return {string|null} Code pays ISO ou null si non détecté
 *
 * @example
 * detectCountryFromLanguage(); // "BE" si navigator.language = "fr-BE"
 */
export const detectCountryFromLanguage = () => {
  try {
    const language = navigator.language || navigator.userLanguage;

    if (!language) return null;

    // Vérifier si la locale complète existe dans notre map
    if (LOCALE_TO_COUNTRY[language]) {
      return LOCALE_TO_COUNTRY[language];
    }

    // Extraire le code pays de la locale (ex: "fr-BE" → "BE")
    const parts = language.split('-');
    if (parts.length === 2) {
      const countryCode = parts[1].toUpperCase();
      // Vérifier que c'est un code pays valide (2 lettres)
      if (countryCode.length === 2) {
        return countryCode;
      }
    }

    return null;
  } catch (error) {
    console.warn('Erreur détection pays via langue:', error);
    return null;
  }
};

/**
 * Détecte le pays via géolocalisation IP (ipapi.co)
 *
 * Service gratuit jusqu'à 30,000 requêtes/mois.
 * Résultat mis en cache pour éviter appels répétés.
 *
 * @return {Promise<string|null>} Code pays ISO ou null si échec
 *
 * @example
 * const country = await detectCountryFromIP();
 * // "BE" si IP belge
 */
export const detectCountryFromIP = async () => {
  // Retourner le cache si disponible
  if (cachedCountry) {
    return cachedCountry;
  }

  try {
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();

    if (data.country_code) {
      cachedCountry = data.country_code;
      return data.country_code;
    }

    return null;
  } catch (error) {
    console.warn('Erreur détection pays via IP:', error);
    return null;
  }
};

/**
 * Détecte automatiquement le pays de l'utilisateur
 *
 * Stratégie en cascade :
 * 1. Langue navigateur (si useLanguage=true)
 * 2. Géolocalisation IP (si useIP=true)
 * 3. Pays par défaut (BE)
 *
 * @param {Object} options - Options de détection
 * @param {boolean} [options.useLanguage=true] - Utiliser détection par langue navigateur
 * @param {boolean} [options.useIP=true] - Utiliser géolocalisation IP
 * @param {string} [options.defaultCountry='BE'] - Pays par défaut
 * @return {Promise<string>} Code pays ISO (toujours retourne un pays)
 *
 * @example
 * // Détection complète avec langue et IP
 * const country = await detectUserCountry();
 *
 * @example
 * // Détection uniquement par IP (plus précis)
 * const country = await detectUserCountry({ useLanguage: false, useIP: true });
 */
export const detectUserCountry = async (options = {}) => {
  const {useLanguage = true, useIP = true, defaultCountry = DEFAULT_COUNTRY} = options;

  // Étape 1 : Essayer via langue navigateur si activé
  if (useLanguage) {
    const languageCountry = detectCountryFromLanguage();
    if (languageCountry) {
      return languageCountry;
    }
  }

  // Étape 2 : Essayer via IP si autorisé
  if (useIP) {
    const ipCountry = await detectCountryFromIP();
    if (ipCountry) {
      return ipCountry;
    }
  }

  // Étape 3 : Fallback sur pays par défaut
  return defaultCountry;
};

/**
 * Hook React pour détecter le pays au montage du composant
 *
 * @param {Object} options - Options de détection
 * @param {boolean} [options.useLanguage=true] - Utiliser détection par langue navigateur
 * @param {boolean} [options.useIP=true] - Utiliser géolocalisation IP
 * @param {string} [options.defaultCountry='BE'] - Pays par défaut
 * @return {Object} État de détection
 * @return {string|null} returns.country - Pays détecté ou null
 * @return {boolean} returns.loading - État de chargement
 *
 * @example
 * const { country, loading } = useCountryDetection();
 * @example
 * const { country, loading } = useCountryDetection({ useLanguage: false, useIP: true });
 */
export const useCountryDetection = (options = {}) => {
  const [country, setCountry] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // Déstructurer les options pour avoir des dépendances primitives stables
  const {useLanguage = true, useIP = true, defaultCountry = DEFAULT_COUNTRY} = options;

  React.useEffect(() => {
    detectUserCountry({useLanguage, useIP, defaultCountry}).then((detected) => {
      setCountry(detected);
      setLoading(false);
    });
  }, [useLanguage, useIP, defaultCountry]);

  return {country, loading};
};
